import { useEffect, useState } from "react";
import {
  Box, Button, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, TextField, MenuItem, IconButton, Chip,
  CircularProgress, Alert, Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { ServerConfig } from "../../../connectors/Config";
import { AuthHelper } from "../../controllers/AuthHelper";
import { useSnackBar } from "../../providers/consumers/useSnackBar";
import { getCourseColorStyle } from "../../../utils/courseColors";

// Converte un File PDF in stringa base64 (senza prefisso data:)
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result || "";
      const base64 = String(result).split(",")[1] || "";
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const PDFCalendarUpload = () => {
  const { addAlert } = useSnackBar();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [rows, setRows] = useState([]);          // corsi individuati (editabili)
  const [warnings, setWarnings] = useState([]);
  const [analyzed, setAnalyzed] = useState(false);
  const [availableCourses, setAvailableCourses] = useState([]); // per il dropdown tipo corso

  // carica elenco corsi (id_corso + nome) per il selettore di tipologia
  useEffect(() => {
    (async () => {
      try {
        const user = await AuthHelper.getUserFromIdToken();
        const res = await (await fetch(`${ServerConfig.AWS.Serverless.API_BASE_URL_2}/prezziario/search/${user.Username}`)).json();
        setAvailableCourses(Array.isArray(res) ? res : []);
      } catch (e) {
        console.warn("Impossibile caricare i corsi", e);
      }
    })();
  }, []);

  const handleFileChange = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    if (f.type !== "application/pdf" && !f.name.toLowerCase().endsWith(".pdf")) {
      addAlert({ message: "Seleziona un file PDF valido", severity: "warning" });
      return;
    }
    setFile(f);
    setRows([]);
    setWarnings([]);
    setAnalyzed(false);
  };

  const handleAnalyze = async () => {
    if (!file) {
      addAlert({ message: "Carica prima un file PDF", severity: "warning" });
      return;
    }
    setLoading(true);
    setWarnings([]);
    try {
      const base64 = await fileToBase64(file);
      const res = await fetch(
        `${ServerConfig.AWS.Serverless.API_BASE_URL_2}/school/courses/parse-pdf`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ pdf_base64: base64 }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Errore analisi PDF");

      const parsed = (data.courses || []).map((c, i) => ({
        _id: `r${i}_${Date.now()}`,
        course_type: c.course_type,
        course_type_id: c.course_type_id,
        date_start: c.date_start,
        date_end: c.date_end,
        notes: c.notes,
        matched: c.matched,
        raw: c.raw,
      }));
      setRows(parsed);
      setWarnings(data.warnings || []);
      setAnalyzed(true);
      if (parsed.length === 0) {
        addAlert({ message: "Nessun corso individuato nel PDF", severity: "info" });
      } else {
        addAlert({ message: `Individuati ${parsed.length} corsi nel PDF`, severity: "success" });
      }
    } catch (e) {
      console.error(e);
      addAlert({ message: e.message || "Errore durante l'analisi del PDF", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const updateRow = (id, field, value) => {
    setRows(prev => prev.map(r => {
      if (r._id !== id) return r;
      const updated = { ...r, [field]: value };
      if (field === "course_type_id") {
        const c = availableCourses.find(ac => ac.id_corso === value);
        updated.course_type = c ? c["nome corso"] : updated.course_type;
        updated.matched = !!value;
      }
      return updated;
    }));
  };

  const removeRow = (id) => setRows(prev => prev.filter(r => r._id !== id));

  const addRow = () => {
    setRows(prev => [...prev, {
      _id: `new_${Date.now()}`,
      course_type: "",
      course_type_id: "",
      date_start: "",
      date_end: "",
      notes: "Aggiunto manualmente",
      matched: false,
      raw: "",
    }]);
  };

  const handleConfirm = async () => {
    // validazione client
    const invalid = rows.filter(r => !r.course_type_id || !r.date_start);
    if (rows.length === 0) {
      addAlert({ message: "Nessun corso da creare", severity: "warning" });
      return;
    }
    if (invalid.length > 0) {
      addAlert({
        message: `${invalid.length} righe incomplete: assegna tipologia corso e data inizio (righe in rosso)`,
        severity: "warning",
      });
      return;
    }
    setCreating(true);
    try {
      const user = await AuthHelper.getUserFromIdToken();
      const payload = {
        centro_id: user.Username,
        courses: rows.map(r => ({
          course_type_id: r.course_type_id,
          date_start: r.date_start,
          date_end: r.date_end || r.date_start,
        })),
      };
      const res = await fetch(
        `${ServerConfig.AWS.Serverless.API_BASE_URL_2}/school/courses/batch-create`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Errore creazione corsi");
      addAlert({ message: data.message || `Creati ${data.count} corsi`, severity: "success" });
      // reset
      setRows([]);
      setFile(null);
      setAnalyzed(false);
      setWarnings([]);
    } catch (e) {
      console.error(e);
      addAlert({ message: e.message || "Errore durante la creazione dei corsi", severity: "error" });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Box sx={{ p: 1 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
        Importa Calendario da PDF
      </Typography>
      <Typography variant="body2" sx={{ color: "#666", mb: 2 }}>
        Carica un calendario corsi in formato PDF (con testo selezionabile). Il sistema
        riconoscerà automaticamente corsi e date; potrai correggerli prima di creare i corsi.
      </Typography>

      {/* Upload */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <Button
          variant="outlined"
          component="label"
          startIcon={<UploadFileIcon />}
        >
          Scegli PDF
          <input type="file" accept=".pdf,application/pdf" hidden onChange={handleFileChange} />
        </Button>
        {file && (
          <Chip label={file.name} onDelete={() => { setFile(null); setRows([]); setAnalyzed(false); }} />
        )}
        <Button
          variant="contained"
          onClick={handleAnalyze}
          disabled={!file || loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {loading ? "Analisi in corso…" : "Analizza PDF"}
        </Button>
      </Box>

      {/* Warnings */}
      {warnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Attenzione:</strong>
          <ul style={{ margin: "4px 0 0 0", paddingLeft: 20 }}>
            {warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </Alert>
      )}

      {/* Tabella anteprima editabile */}
      {analyzed && rows.length > 0 && (
        <>
          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell sx={{ fontWeight: 700 }}>Tipologia corso</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Data inizio</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Data fine</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Note</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => {
                  const incomplete = !r.course_type_id || !r.date_start;
                  return (
                    <TableRow
                      key={r._id}
                      sx={{ backgroundColor: incomplete ? "#fdecea" : "inherit" }}
                    >
                      <TableCell sx={{ minWidth: 240 }}>
                        <TextField
                          select
                          size="small"
                          fullWidth
                          value={r.course_type_id || ""}
                          onChange={(e) => updateRow(r._id, "course_type_id", e.target.value)}
                          error={!r.course_type_id}
                          helperText={!r.course_type_id ? (r.course_type ? `Non riconosciuto: "${r.course_type}"` : "Seleziona corso") : ""}
                        >
                          <MenuItem value="" disabled>Seleziona corso</MenuItem>
                          {availableCourses.map(c => (
                            <MenuItem key={c.id_corso} value={c.id_corso}>{c["nome corso"]}</MenuItem>
                          ))}
                        </TextField>
                        {r.course_type_id && (() => {
                          const st = getCourseColorStyle(r.course_type);
                          return (
                            <span style={{ display: "inline-block", marginTop: 4, padding: "1px 8px", borderRadius: 10, fontSize: 11, ...st }}>
                              {r.course_type}
                            </span>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="date"
                          size="small"
                          value={r.date_start || ""}
                          onChange={(e) => updateRow(r._id, "date_start", e.target.value)}
                          error={!r.date_start}
                          InputLabelProps={{ shrink: true }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="date"
                          size="small"
                          value={r.date_end || ""}
                          onChange={(e) => updateRow(r._id, "date_end", e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>
                        <Typography variant="caption" sx={{ color: "#888" }}>
                          {r.notes}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Rimuovi riga">
                          <IconButton size="small" color="error" onClick={() => removeRow(r._id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={addRow}>
              Aggiungi riga
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleConfirm}
              disabled={creating}
              startIcon={creating ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {creating ? "Creazione in corso…" : `Conferma e Crea Corsi (${rows.length})`}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default PDFCalendarUpload;
